/* eslint-disable react/prop-types */
/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Radio,
  Button,
  Spin,
  message,
  Select,
} from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { checkToken } from 'api/utils';
import { getJustificacionesOptimizedReloj } from 'api/configuraciones/clock/Justificacion/Justificaciones';
import { getAsignacionPlazasOptimized } from 'api/catalogos/plazas';

const ModalExportarReporte = ({
  visible,
  onCancel,
  parentForm,
  periodosFiscales = [],
  meses = [],
}) => {
  const [exportType, setExportType] = useState('empleado');
  const [loading, setLoading] = useState(false);
  const [mesInicio, setMesInicio] = useState();
  const [mesFin, setMesFin] = useState();
  const [periodoFiscal, setPeriodoFiscal] = useState();

  // Obtener filtros actuales del formulario padre
  const empleadoId = parentForm?.getFieldValue('empleado_number');
  const empleadoIdentified = parentForm?.getFieldValue('empleado_identified');
  const mesId = parentForm?.getFieldValue('mes');
  const periodoFiscalId = parentForm?.getFieldValue('periodo_fiscal');

  // Sincronizar año y meses iniciales al abrir el modal
  useEffect(() => {
    if (visible) {
      setPeriodoFiscal(periodoFiscalId);
      setMesInicio(mesId);
      setMesFin(mesId);
    }
  }, [mesId, periodoFiscalId, visible]);

  // Cambiar por defecto a 'todos' si no hay empleado seleccionado
  useEffect(() => {
    if (!empleadoId) {
      setExportType('todos');
    } else {
      setExportType('empleado');
    }
  }, [empleadoId, visible]);

  const handleExport = async () => {
    if (!mesInicio || !mesFin) {
      message.warning('Por favor, seleccione los meses inicial y final.');
      return;
    }
    if (mesInicio > mesFin) {
      message.warning('El mes inicial no puede ser mayor que el mes final.');
      return;
    }

    try {
      setLoading(true);
      await checkToken();

      // Parámetros de búsqueda para justificaciones (excluyendo el mes_id que varía)
      const baseParams = {
        periodo_fiscal_id: periodoFiscal,
      };

      if (exportType === 'empleado') {
        if (!empleadoId) {
          message.warning('Por favor, seleccione un empleado en la pantalla principal.');
          setLoading(false);
          return;
        }
        baseParams.empleado_id = empleadoId;
      }

      message.loading({ content: 'Obteniendo justificaciones...', key: 'exportReporte' });

      // Generar rango de meses
      const rangeOfMonths = [];
      for (let m = mesInicio; m <= mesFin; m += 1) {
        rangeOfMonths.push(m);
      }

      // Ejecutar peticiones en paralelo para todos los meses en el rango, junto con las plazas
      const justsPromises = rangeOfMonths.map((mId) => getJustificacionesOptimizedReloj({
        ...baseParams,
        mes_id: mId,
      }));

      const [justificacionesResults, plazasRaw] = await Promise.all([
        Promise.all(justsPromises),
        getAsignacionPlazasOptimized(),
      ]);

      // Aplanar los resultados de los meses
      const justificaciones = justificacionesResults.flat();
      const plazas = Array.isArray(plazasRaw) ? plazasRaw : (plazasRaw?.results || []);

      if (justificaciones.length === 0) {
        message.destroy();
        message.warning('No se encontraron justificaciones para el periodo seleccionado.');
        setLoading(false);
        return;
      }

      message.loading({ content: 'Procesando reporte...', key: 'exportReporte' });

      // Extraer número de empleado de cadenas tipo "1986 - NOMBRE"
      const getEmpNum = (str) => {
        if (!str) return '';
        const match = str.match(/^(\d+)/);
        return match ? match[1] : '';
      };

      // Crear mapa de asignaciones en memoria para búsquedas O(1)
      // mapea número de empleado -> plaza y area
      const plazasMap = new Map();
      plazas.forEach((p) => {
        const empNum = getEmpNum(p.numero_y_nombre_empleado);
        if (empNum) {
          plazasMap.set(empNum, {
            plaza: p.clave_y_descripcion || '',
            area: p.directorio_area_clave_y_nombre || '',
          });
        }
      });

      // Mapear los datos al formato del Excel requerido
      const excelData = justificaciones.map((record) => {
        // Obtener datos de plaza/área desde nuestro mapa en memoria usando el número de empleado
        const empNum = getEmpNum(record.empleado_numero_y_nombre_completo)
          || String(record.empleado_id || '');
        const empPlazaData = plazasMap.get(empNum) || {};

        // Extraer número y fecha de oficio a partir de `no_oficio_fecha`
        // Formato esperado: "numero_oficio/fecha_oficio"
        let numeroOficio = '';
        let fechaOficio = '';
        if (record.no_oficio_fecha) {
          const parts = record.no_oficio_fecha.split('/');
          numeroOficio = parts[0] || '';
          fechaOficio = parts[1] || '';
        }

        // Tipo de incidencia es `tipo_de_evaluacion_de_asistencia_nombre` o fallback a `evaluacion`
        const tipoIncidencia = record.tipo_de_evaluacion_de_asistencia_nombre || record.evaluacion || '';

        return {
          'Número y Nombre del Empleado': record.empleado_numero_y_nombre_completo || '',
          'Clave y Nombre de la Plaza': empPlazaData.plaza || '',
          'Clave y Nombre del Área': empPlazaData.area || '',
          'Tipo de Incidencia': tipoIncidencia,
          'Fecha de Incidencia': record.fecha_carga_reporte || '',
          'Fecha de Justificación': fechaOficio || '', // Usamos la fecha de oficio como fecha de justificación aproximada
          'Número de Oficio': numeroOficio,
          'Fecha de Oficio': fechaOficio,
          Estatus: record.estados_globales_name || '',
        };
      });

      // Generar periodString y añoText para el título y nombre de archivo
      const mesInicioName = meses.find((m) => m.id === mesInicio)?.nombre || String(mesInicio);
      const mesFinName = meses.find((m) => m.id === mesFin)?.nombre || String(mesFin);
      const periodString = mesInicio === mesFin ? mesInicioName : `${mesInicioName}_a_${mesFinName}`;
      const añoText = periodosFiscales.find((item) => item.id === periodoFiscal)?.descripcion || '';

      const excelRows = [];

      // Título
      excelRows.push(['SISTEMA DE NÓMINA']);
      excelRows.push(['REPORTE DE JUSTIFICACIONES']);
      excelRows.push([`Periodo: ${periodString} ${añoText}`]);
      excelRows.push([]);

      // Encabezados de columnas
      const headers = [
        'Número y Nombre del Empleado',
        'Clave y Nombre de la Plaza',
        'Clave y Nombre del Área',
        'Tipo de Incidencia',
        'Fecha de Incidencia',
        'Fecha de Justificación',
        'Número de Oficio',
        'Fecha de Oficio',
        'Estatus',
      ];
      excelRows.push(headers);

      // Datos
      excelData.forEach((row) => {
        excelRows.push([
          row['Número y Nombre del Empleado'],
          row['Clave y Nombre de la Plaza'],
          row['Clave y Nombre del Área'],
          row['Tipo de Incidencia'],
          row['Fecha de Incidencia'],
          row['Fecha de Justificación'],
          row['Número de Oficio'],
          row['Fecha de Oficio'],
          row.Estatus,
        ]);
      });

      // Crear el libro Excel usando aoa_to_sheet
      const worksheet = XLSX.utils.aoa_to_sheet(excelRows);

      // Congelar la fila de encabezados. Como los datos comienzan en la fila 6 (índice 5 base 0),
      // congelamos las primeras 5 filas (título + info + headers)
      worksheet['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: 5 }];

      // Ajustar anchos de columna automáticamente
      const colWidths = headers.map((header, colIdx) => {
        const lengths = excelRows.map((r) => String(r[colIdx] || '').length);
        return { wch: Math.max(header.length, ...lengths) + 3 };
      });
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Justificaciones');

      // Descargar el archivo
      const currentDate = moment().format('YYYY-MM-DD');

      const filename = exportType === 'empleado'
        ? `Reporte_Justificaciones_${empleadoId}_${periodString}_${currentDate}.xlsx`
        : `Reporte_Justificaciones_Todos_${periodString}_${currentDate}.xlsx`;

      XLSX.writeFile(workbook, filename);

      message.destroy();
      message.success(`Se exportaron ${excelData.length} registros correctamente.`);
      onCancel();
    } catch (err) {
      console.error(err);
      message.destroy();
      message.error('Ocurrió un error al generar el reporte.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Exportar Reporte de Justificaciones"
      open={visible}
      onCancel={onCancel}
      width={550}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading} type="link">
          Cancelar
        </Button>,
        <Button
          key="export"
          type="link"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={loading}
        >
          Exportar Excel
        </Button>,
      ]}
    >
      <Spin spinning={loading} tip="Obteniendo y procesando datos...">
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4 }}><strong>Año:</strong></div>
              <Select
                value={periodoFiscal}
                onChange={setPeriodoFiscal}
                style={{ width: '100%' }}
              >
                {periodosFiscales.map((p) => (
                  <Select.Option key={p.id} value={p.id}>
                    {p.descripcion}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4 }}><strong>Mes Inicial:</strong></div>
              <Select
                value={mesInicio}
                onChange={setMesInicio}
                style={{ width: '100%' }}
              >
                {meses.map((m) => (
                  <Select.Option key={m.id} value={m.id}>
                    {m.nombre}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4 }}><strong>Mes Final:</strong></div>
              <Select
                value={mesFin}
                onChange={setMesFin}
                style={{ width: '100%' }}
              >
                {meses.map((m) => (
                  <Select.Option key={m.id} value={m.id}>
                    {m.nombre}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        <Radio.Group
          onChange={(e) => setExportType(e.target.value)}
          value={exportType}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <Radio value="empleado" disabled={!empleadoId}>
            <span style={{ fontWeight: exportType === 'empleado' ? 'bold' : 'normal' }}>
              Exportar por Empleado
            </span>
            {empleadoId ? (
              <div style={{ fontSize: '12px', color: '#666', marginLeft: 24 }}>
                Se exportará el empleado seleccionado:
                {' '}
                <strong>
                  {empleadoIdentified}
                </strong>
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#ff4d4f', marginLeft: 24 }}>
                ⚠️ Seleccione un empleado en la pantalla principal para habilitar esta opción.
              </div>
            )}
          </Radio>
          <Radio value="todos">
            <span style={{ fontWeight: exportType === 'todos' ? 'bold' : 'normal' }}>
              Exportar Todos los Empleados
            </span>
            <div style={{ fontSize: '12px', color: '#666', marginLeft: 24 }}>
              Se exportarán las justificaciones de todos los empleados para el periodo indicado.
            </div>
          </Radio>
        </Radio.Group>
      </Spin>
    </Modal>
  );
};

export default ModalExportarReporte;
